import { useRef, useEffect } from 'react';
import { trackEvent, eventNameConstants } from 'dibs-tracking';
import { graphql, readInlineData } from 'react-relay/legacy.js';

import {
    type interiorsPhotosTracking_searchBrowsePhoto$data,
    type interiorsPhotosTracking_searchBrowsePhoto$key,
} from './__generated__/interiorsPhotosTracking_searchBrowsePhoto.graphql.ts';
import {
    type interiorsPhotosTracking_tradePhoto$data,
    type interiorsPhotosTracking_tradePhoto$key,
} from './__generated__/interiorsPhotosTracking_tradePhoto.graphql.ts';

export const trackPhotoInteraction = ({
    action,
    label,
}: {
    action: string;
    label: string;
}): void => {
    trackEvent({
        category: 'trade firm project image interaction',
        action,
        label,
        eventName: eventNameConstants.EVENT_PRODUCT_INTERACTION,
        interaction_type: action,
        step_interaction_name: label,
    });
};

type Photo =
    | interiorsPhotosTracking_tradePhoto$data
    | interiorsPhotosTracking_searchBrowsePhoto$data;

const useTrackPhotoImpression = ({ photo }: { photo?: Photo | null }): void => {
    const photoId = photo?.serviceId || '';

    const firedImpressionsRef = useRef<string[]>([]);

    useEffect(() => {
        if (photoId && !firedImpressionsRef.current.includes(photoId)) {
            trackPhotoInteraction({
                action: 'photo shoppable impression',
                label: 'shoppable / not shoppable',
            });
            firedImpressionsRef.current.push(photoId);
        }
    }, [photoId]);
};

export const useTrackSearchBrowsePhotoPageview = (
    tradePhotoRef: interiorsPhotosTracking_searchBrowsePhoto$key | null | undefined
): void => {
    const searchBrowsePhoto = readInlineData(
        graphql`
            fragment interiorsPhotosTracking_searchBrowsePhoto on PhotosSearchBrowsePhoto @inline {
                serviceId
            }
        `,
        tradePhotoRef
    );
    useTrackPhotoImpression({ photo: searchBrowsePhoto });
};

export const useTrackTradePhotoPageview = (
    tradePhotoRef: interiorsPhotosTracking_tradePhoto$key | null | undefined
): void => {
    const tradePhoto = readInlineData(
        graphql`
            fragment interiorsPhotosTracking_tradePhoto on TradePhoto @inline {
                serviceId
            }
        `,
        tradePhotoRef
    );
    useTrackPhotoImpression({ photo: tradePhoto });
};
